import { create } from 'zustand';
import EsriMap from '@arcgis/core/Map.js';
import MapView from '@arcgis/core/views/MapView.js';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer.js';
import Basemap from '@arcgis/core/Basemap.js';
import { initArcgisRequest } from './portal';
import {
  initLayerRegistry,
  selectLayerIndex,
  useLayerRegistry,
} from './layers/registry';
import { applyLayerStyling, getOrCreateFeatureLayer } from './layers/factory';
import { installClickSelect, installHoverHighlight } from './selection';
import { registerStandardWidgets } from './widgets';
import { ensureMeasurementsLayer } from './measurements';
import { ensureSketchesLayer } from './sketches';
import { useViewStateStore } from '@/state/viewStateStore';

const DEFAULT_BASEMAP = 'streets-vector';
const HIGHLIGHT_LAYER_ID = '__rcmc_highlights';
const SELECTION_LAYER_ID = '__rcmc_user_selection';
const BUFFER_LAYER_ID = '__rcmc_buffers';
const MARKER_LAYER_ID = '__rcmc_markers';

type MapStatus = 'idle' | 'loading' | 'ready' | 'error';

type MapStoreState = {
  status: MapStatus;
  error: string | null;
  basemapId: string;
  setStatus: (status: MapStatus) => void;
  setError: (error: string | null) => void;
  setBasemapId: (id: string) => void;
};

export const useMapStore = create<MapStoreState>((set) => ({
  status: 'idle',
  error: null,
  basemapId: DEFAULT_BASEMAP,
  setStatus: (status) => set({ status }),
  setError: (error) => set({ error }),
  setBasemapId: (basemapId) => set({ basemapId }),
}));

let map: EsriMap | null = null;
let view: MapView | null = null;
let highlightLayer: GraphicsLayer | null = null;
let selectionLayer: GraphicsLayer | null = null;
let bufferLayer: GraphicsLayer | null = null;
let markerLayer: GraphicsLayer | null = null;
let measurementsLayer: GraphicsLayer | null = null;
let sketchesLayer: GraphicsLayer | null = null;
let initPromise: Promise<MapView> | null = null;
let teardownInteractions: (() => void) | null = null;
let registryUnsubscribe: (() => void) | null = null;

function installViewStateTracking(v: MapView): () => void {
  const setCoords = useViewStateStore.getState().setCoords;
  const setScale = useViewStateStore.getState().setScale;
  const setZoom = useViewStateStore.getState().setZoom;

  if (typeof v.scale === 'number') setScale(v.scale);
  if (typeof v.zoom === 'number') setZoom(v.zoom);

  let pendingPt: { x: number; y: number } | null = null;
  let rafId: number | null = null;

  const moveHandle = v.on('pointer-move', (e) => {
    pendingPt = { x: e.x, y: e.y };
    if (rafId !== null) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      const screen = pendingPt;
      pendingPt = null;
      if (!screen) return;
      const pt = v.toMap(screen);
      if (!pt) return;
      setCoords({
        x: pt.x,
        y: pt.y,
        wkid: pt.spatialReference?.wkid ?? undefined,
      });
    });
  });
  const leaveHandle = v.on('pointer-leave', () => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
    pendingPt = null;
    setCoords(null);
  });
  const scaleWatch = v.watch('scale', (scale: number) => {
    if (typeof scale === 'number') setScale(scale);
  });
  const zoomWatch = v.watch('zoom', (zoom: number) => {
    if (typeof zoom === 'number') setZoom(zoom);
  });

  return () => {
    moveHandle.remove();
    leaveHandle.remove();
    scaleWatch.remove();
    zoomWatch.remove();
    if (rafId !== null) cancelAnimationFrame(rafId);
  };
}

function ensureMap(): EsriMap {
  if (map) return map;
  map = new EsriMap({ basemap: DEFAULT_BASEMAP });
  bufferLayer = new GraphicsLayer({ id: BUFFER_LAYER_ID, title: 'Buffers', listMode: 'hide' });
  highlightLayer = new GraphicsLayer({
    id: HIGHLIGHT_LAYER_ID,
    title: 'Highlights',
    listMode: 'hide',
  });
  markerLayer = new GraphicsLayer({ id: MARKER_LAYER_ID, title: 'Markers', listMode: 'hide' });
  selectionLayer = new GraphicsLayer({
    id: SELECTION_LAYER_ID,
    title: 'User selection',
    listMode: 'hide',
  });
  measurementsLayer = ensureMeasurementsLayer();
  sketchesLayer = ensureSketchesLayer();
  // order: feature layers (added later) < buffers < highlights < markers < selection < sketches < measurements
  map.add(bufferLayer);
  map.add(highlightLayer);
  map.add(markerLayer);
  map.add(selectionLayer);
  map.add(sketchesLayer);
  map.add(measurementsLayer);
  return map;
}

function reorderOverlayLayers(target: EsriMap): void {
  const overlays = [
    bufferLayer,
    highlightLayer,
    markerLayer,
    selectionLayer,
    sketchesLayer,
    measurementsLayer,
  ].filter((l): l is GraphicsLayer => !!l);
  for (const overlay of overlays) {
    if (!target.layers.includes(overlay)) target.add(overlay);
    target.reorder(overlay, target.layers.length - 1);
  }
}

async function addStartupLayers(target: EsriMap): Promise<void> {
  await initLayerRegistry();
  const records = selectLayerIndex();
  const startup = records.filter((r) => r.flags.has('default'));

  const featureLayers = startup.map((r) => getOrCreateFeatureLayer(r));
  for (const fl of featureLayers) {
    if (!target.layers.includes(fl)) target.add(fl);
  }

  await Promise.allSettled(featureLayers.map((fl) => fl.load()));

  for (const r of startup) {
    applyLayerStyling(r, records);
  }

  reorderOverlayLayers(target);
}

function watchRegistryForStyling(target: EsriMap): () => void {
  return useLayerRegistry.subscribe((state, prev) => {
    if (state.records === prev.records) return;
    const prevById = new Map(prev.records.map((r) => [r.id, r]));
    for (const r of state.records) {
      const before = prevById.get(r.id);
      if (!before || before.status !== r.status || before.probe !== r.probe) {
        const layer = target.layers.find((l) => l.id === r.id);
        if (layer && r.status === 'ready') {
          applyLayerStyling(r, state.records);
        }
      }
    }
  });
}

async function frameToFirstStartupLayer(targetView: MapView): Promise<void> {
  const startup = selectLayerIndex().filter((r) => r.flags.has('default'));
  if (startup.length === 0) return;
  const first = targetView.map?.layers.find((l) => l.id === startup[0].id);
  if (!first) return;
  try {
    await first.when();
    const fullExtent = (first as { fullExtent?: __esri.Extent }).fullExtent;
    if (fullExtent) {
      await targetView.goTo(fullExtent.expand(1.05));
    }
  } catch {
    // non-fatal
  }
}

export async function initMapView(container: HTMLDivElement): Promise<MapView> {
  if (initPromise) {
    const v = await initPromise;
    if (v.container !== container) {
      v.container = container;
    }
    return v;
  }
  initPromise = (async () => {
    useMapStore.getState().setStatus('loading');
    try {
      await initArcgisRequest();
      const m = ensureMap();
      await addStartupLayers(m);

      const v = new MapView({
        container,
        map: m,
        center: [39.826, 21.422],
        zoom: 12,
        popupEnabled: true,
      });
      view = v;
      await v.when();

      registerStandardWidgets(v);

      const tearHover = installHoverHighlight(v);
      const tearClick = selectionLayer
        ? installClickSelect(v, selectionLayer)
        : () => {};
      const tearViewState = installViewStateTracking(v);
      teardownInteractions = () => {
        tearHover();
        tearClick();
        tearViewState();
      };

      registryUnsubscribe = watchRegistryForStyling(m);

      await frameToFirstStartupLayer(v);
      useMapStore.getState().setStatus('ready');
      return v;
    } catch (err) {
      const message = (err as Error).message ?? 'Map failed to initialize';
      useMapStore.getState().setError(message);
      useMapStore.getState().setStatus('error');
      throw err;
    }
  })();
  return initPromise;
}

export function getMapView(): MapView | null {
  return view;
}

export function getMap(): EsriMap | null {
  return map;
}

export function getHighlightLayer(): GraphicsLayer | null {
  return highlightLayer;
}

export function bringOverlayLayersToTop(): void {
  if (map) reorderOverlayLayers(map);
}

export function getSelectionLayer(): GraphicsLayer | null {
  return selectionLayer;
}

export function getBufferLayer(): GraphicsLayer | null {
  return bufferLayer;
}

export function getMarkerLayer(): GraphicsLayer | null {
  return markerLayer;
}

export async function ensureLayerOnMap(id: string): Promise<__esri.Layer | null> {
  const target = ensureMap();
  const record = selectLayerIndex().find((r) => r.id === id);
  if (!record) return null;
  const fl = getOrCreateFeatureLayer(record);
  if (!target.layers.includes(fl)) target.add(fl);
  await fl.load().catch(() => undefined);
  applyLayerStyling(record, selectLayerIndex());
  reorderOverlayLayers(target);
  return fl;
}

export function setLayerVisible(id: string, visible: boolean): void {
  const target = map;
  if (!target) return;
  const layer = target.layers.find((l) => l.id === id);
  if (layer) {
    layer.visible = visible;
    return;
  }
  if (visible) void ensureLayerOnMap(id);
}

export function setBasemapById(id: string): void {
  const m = ensureMap();
  m.basemap = Basemap.fromId(id);
  useMapStore.getState().setBasemapId(id);
}

export function destroyMapView(): void {
  teardownInteractions?.();
  teardownInteractions = null;
  registryUnsubscribe?.();
  registryUnsubscribe = null;
  if (view) {
    view.container = null as unknown as HTMLDivElement;
    view.destroy();
    view = null;
  }
  initPromise = null;
}
